import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { formatDistanceToNow, isValid, parseISO } from "date-fns";

interface JobShiftScheduleAttributes {
  rotation?: Array<{ type: string; duration: number }>;
  daysOfWeek?: string[];
  minimumRequiredDaysPerWeek?: number;
  workdaysRequired?: string;
  notes?: string;
}

const WEEKDAYS = new Set(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"]);
const WEEKENDS = new Set(["SATURDAY", "SUNDAY"]);

export function computeScheduleType(
  jobShift: JobShiftScheduleAttributes
): "4_on_2_off" | "flexible" | "fixed" | "other" | undefined {
  if (
    jobShift.rotation?.length === 2 &&
    jobShift.rotation[0]?.type === "on" &&
    jobShift.rotation[0]?.duration === 4 &&
    jobShift.rotation[1]?.type === "off" &&
    jobShift.rotation[1]?.duration === 2
  ) {
    return "4_on_2_off";
  }

  if (
    (!jobShift.rotation || jobShift.rotation.length === 0) &&
    jobShift.minimumRequiredDaysPerWeek !== undefined
  ) {
    return "flexible";
  }

  if (
    (!jobShift.rotation || jobShift.rotation.length === 0) &&
    jobShift.daysOfWeek &&
    jobShift.daysOfWeek.length > 0 &&
    jobShift.minimumRequiredDaysPerWeek === undefined
  ) {
    return "fixed";
  }

  if (
    (!jobShift.rotation || jobShift.rotation.length === 0) &&
    (!jobShift.daysOfWeek || jobShift.daysOfWeek.length === 0) &&
    jobShift.notes
  ) {
    return "other";
  }

  return undefined;
}

export function computeWorkdaysRequired(
  daysOfWeek: string[] | undefined
): "weekdays" | "weekends" | "both" | undefined {
  if (!daysOfWeek || daysOfWeek.length === 0) {
    return undefined;
  }

  const hasWeekdays = daysOfWeek.some((day) => WEEKDAYS.has(day.toUpperCase()));
  const hasWeekends = daysOfWeek.some((day) => WEEKENDS.has(day.toUpperCase()));

  if (hasWeekdays && hasWeekends) {
    return "both";
  }

  if (hasWeekdays) {
    return "weekdays";
  }

  if (hasWeekends) {
    return "weekends";
  }

  return undefined;
}

const DAY_ABBREVIATIONS: Record<string, string> = {
  [DayOfTheWeek.MONDAY]: "Mon",
  [DayOfTheWeek.TUESDAY]: "Tue",
  [DayOfTheWeek.WEDNESDAY]: "Wed",
  [DayOfTheWeek.THURSDAY]: "Thu",
  [DayOfTheWeek.FRIDAY]: "Fri",
  [DayOfTheWeek.SATURDAY]: "Sat",
  [DayOfTheWeek.SUNDAY]: "Sun",
};

const DAY_ORDER = [
  DayOfTheWeek.MONDAY,
  DayOfTheWeek.TUESDAY,
  DayOfTheWeek.WEDNESDAY,
  DayOfTheWeek.THURSDAY,
  DayOfTheWeek.FRIDAY,
  DayOfTheWeek.SATURDAY,
  DayOfTheWeek.SUNDAY,
];

/**
 * Formats fixed days with abbreviations and consecutive day ranges
 * Example: ["monday", "tuesday", "wednesday", "friday"] -> "Mon-Wed, Fri"
 */
export function formatFixedDays(daysOfWeek: string[]): string {
  if (daysOfWeek.length === 0) {
    return "";
  }

  const sortedDays = [...daysOfWeek].sort(
    (a, b) =>
      DAY_ORDER.indexOf(a.toUpperCase() as DayOfTheWeek) -
      DAY_ORDER.indexOf(b.toUpperCase() as DayOfTheWeek)
  );

  const groups: string[][] = [];
  let currentGroup: string[] = [sortedDays[0]];

  for (let index = 1; index < sortedDays.length; index += 1) {
    const currentDayIndex = DAY_ORDER.indexOf(sortedDays[index].toUpperCase() as DayOfTheWeek);
    const previousDayIndex = DAY_ORDER.indexOf(sortedDays[index - 1].toUpperCase() as DayOfTheWeek);

    if (currentDayIndex === previousDayIndex + 1) {
      // Consecutive day
      currentGroup.push(sortedDays[index]);
    } else {
      // Non-consecutive, start new group
      groups.push(currentGroup);
      currentGroup = [sortedDays[index]];
    }
  }

  groups.push(currentGroup);

  return groups
    .map((group) => {
      if (group.length >= 3) {
        // Show range for 3+ consecutive days
        const firstDay = DAY_ABBREVIATIONS[group[0].toUpperCase()];
        const lastDay = group.at(-1);
        const lastDayAbbreviation = lastDay ? DAY_ABBREVIATIONS[lastDay.toUpperCase()] : "";
        return `${firstDay}-${lastDayAbbreviation}`;
      }

      // Show individual days for 1-2 days
      return group.map((day) => DAY_ABBREVIATIONS[day.toUpperCase()]).join(", ");
    })
    .join(", ");
}

/**
 * Formats flexible schedule with day count and workdays requirement
 * Example: (3, "weekdays") -> "3 days / wk, weekdays only"
 * Example: (4, "both") -> "4 days / wk, mix of (weekdays, weekends)"
 */
export function formatFlexibleSchedule(
  daysPerWeek: number | undefined,
  workdaysRequired?: "weekdays" | "weekends" | "both"
): string {
  const hasSpecificDayCount = daysPerWeek !== undefined && daysPerWeek > 0;

  let basePart: string;
  if (hasSpecificDayCount) {
    const dayLabel = daysPerWeek === 1 ? "day" : "days";
    basePart = `${daysPerWeek} ${dayLabel} / wk`;
  } else {
    basePart = "Flexible days / wk";
  }

  if (!workdaysRequired) {
    return basePart;
  }

  let workdaysPart: string;
  if (workdaysRequired === "both") {
    workdaysPart = "with a mix of (weekdays, weekends)";
  } else if (workdaysRequired === "weekdays") {
    workdaysPart = "weekdays only";
  } else {
    workdaysPart = "weekends only";
  }

  return `${basePart}, ${workdaysPart}`;
}

interface PlacementDateInfo {
  action: "created" | "updated" | "confirmed";
  formattedDistance: string;
  latestDate: Date;
}

/**
 * Converts a value to a valid Date object if possible
 * Handles Date objects, date strings, and invalid values
 */
function toValidDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return isValid(value) ? value : undefined;
  }

  if (typeof value === "string") {
    const date = parseISO(value);
    return isValid(date) ? date : undefined;
  }

  return undefined;
}

export function getPlacementLatestDateInfo(
  createdAt: Date | string | undefined,
  updatedAt: Date | string | undefined,
  confirmedAt: Date | string | undefined
): PlacementDateInfo | undefined {
  const validCreatedAt = toValidDate(createdAt);
  const validUpdatedAt = toValidDate(updatedAt);
  const validConfirmedAt = toValidDate(confirmedAt);

  // Prefer confirmedAt over updatedAt when present
  const dates: Array<{ date: Date; action: "created" | "updated" | "confirmed" }> = [];

  if (validCreatedAt) {
    dates.push({ date: validCreatedAt, action: "created" });
  }

  if (validConfirmedAt) {
    dates.push({ date: validConfirmedAt, action: "confirmed" });
  } else if (validUpdatedAt) {
    dates.push({ date: validUpdatedAt, action: "updated" });
  }

  if (dates.length === 0) {
    return undefined;
  }

  const latest = dates.reduce((latest, current) => (current.date > latest.date ? current : latest));

  return {
    action: latest.action,
    formattedDistance: formatDistanceToNow(latest.date),
    latestDate: latest.date,
  };
}
